import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { CurrencySelect } from "@/components/CurrencySelect";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtMoney } from "@/lib/api";

const loadRzp = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true);
  s.onerror = () => res(false);
  document.body.appendChild(s);
});

export const CheckoutBox = ({ type, item, onDone }) => {
  const { currency, config } = useCurrency();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gstin, setGstin] = useState("");
  const [billName, setBillName] = useState("");
  useEffect(() => { loadRzp(); }, []);
  if (!config) return <div className="h-72 rounded-2xl bg-muted animate-pulse" />;
  const src = type === "plan" ? config.plans : config.packs;
  const it = src[item];
  if (!it) return <p className="text-muted-foreground">Unknown item. <Link to="/pricing" className="underline">Back to pricing</Link></p>;
  const total = it.prices[currency];
  const tax = currency === "INR" ? Math.round((total - total / 1.18) * 100) / 100 : 0;
  const gstOk = !gstin || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin);

  const pay = async () => {
    if (!agree) return toast.error("Please accept the Terms to continue");
    if (!gstOk) return toast.error("Please enter a valid 15-character GSTIN or leave it blank");
    setBusy(true);
    try {
      const { data } = await api.post("/billing/order", { item_type: type, item_id: item, currency, accept_terms: agree, customer_gstin: currency === "INR" ? gstin : "", billing_name: billName });
      const ok = await loadRzp();
      if (!ok) throw new Error("Could not load payment window");
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id, name: "Sliance",
        description: data.name, prefill: { email: data.email, name: data.user_name }, theme: { color: "#4f46e5" },
        handler: async (r) => {
          try {
            await api.post("/billing/verify", r);
            await refresh();
            toast.success("Payment successful — your workspace is updated");
            onDone ? onDone() : nav("/app/billing");
          } catch (e) { toast.error(errMsg(e)); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="card-box p-6 md:p-8" data-testid="checkout-box">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="eyebrow">{type === "plan" ? "Subscription · monthly" : "AI credit pack"}</div>
          <h3 className="mt-2 text-2xl font-bold" data-testid="checkout-item-name">{it.name}</h3>
        </div>
        <CurrencySelect testId="checkout-currency-select" />
      </div>
      <div className="mt-6 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">Taxable value</span><span data-testid="checkout-subtotal">{fmtMoney(total - tax, currency, config.symbols)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">{currency === "INR" ? "GST 18% (included)" : "Taxes"}</span><span data-testid="checkout-gst">{currency === "INR" ? fmtMoney(tax, currency, config.symbols) : "As applicable"}</span></div>
        <div className="flex justify-between border-t pt-3 text-base font-semibold"><span>Total due today</span><span data-testid="checkout-total">{fmtMoney(total, currency, config.symbols)}</span></div>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <Input placeholder="Billing name (company)" value={billName} onChange={(e) => setBillName(e.target.value)} data-testid="checkout-billing-name" />
        {currency === "INR" && <div><Input placeholder="GSTIN (optional, for GST invoice)" value={gstin} maxLength={15} onChange={(e) => setGstin(e.target.value.toUpperCase().trim())} data-testid="checkout-gstin-input" className={gstOk ? "" : "border-red-500"} />
          {!gstOk && <p className="mt-1 text-[11px] text-red-500" data-testid="checkout-gstin-error">Invalid GSTIN format</p>}</div>}
      </div>
      <label className="mt-6 flex items-start gap-3 rounded-xl border p-4 cursor-pointer" data-testid="checkout-terms-label">
        <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="checkout-terms-checkbox" className="mt-0.5" />
        <span className="text-xs leading-relaxed text-muted-foreground">
          I agree to the <Link to="/legal/terms" target="_blank" className="underline text-foreground">Terms of Service</Link>, <Link to="/legal/privacy" target="_blank" className="underline text-foreground">Privacy Policy</Link>, <Link to="/legal/gdpr" target="_blank" className="underline text-foreground">GDPR & Data Protection</Link> and <Link to="/legal/refund" target="_blank" className="underline text-foreground">Refund Policy</Link>. I understand Sliance provides tools only and makes <strong className="text-foreground">no commitment or guarantee of marketing success</strong>, and that there is no long-term commitment.
        </span>
      </label>
      {!config.razorpay_enabled && (
        <p className="mt-4 rounded-xl bg-[#f59e0b]/10 p-3 text-xs text-[#b45309] dark:text-[#fbbf24]" data-testid="checkout-payments-pending">
          Online payments are being configured. Contact <a href="mailto:support@sliance.com" className="underline">support@sliance.com</a> to activate your plan.
        </p>
      )}
      <button onClick={pay} disabled={!agree || busy || !config.razorpay_enabled} className="btn-brand w-full mt-5 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="checkout-pay-btn">
        <Lock className="h-4 w-4" /> {busy ? "Processing..." : `Pay ${fmtMoney(total, currency, config.symbols)}`}
      </button>
      <p className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Secure payments by Razorpay{config.mode === "test" ? " · TEST MODE (admin)" : ""} · Billed by {config.seller?.name}</p>
    </div>
  );
};

export default function Checkout() {
  const p = new URLSearchParams(window.location.search);
  return (
    <section className="py-16 md:py-24" data-testid="checkout-page">
      <div className="container-x max-w-2xl">
        <h1 className="text-4xl font-extrabold tracking-tight mb-8">Checkout</h1>
        <CheckoutBox type={p.get("type") || "plan"} item={p.get("item") || "growth"} />
      </div>
    </section>
  );
}
